import { Head, router } from "@inertiajs/react"
import { Building2 } from "lucide-react"
import { useState, type ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { Field } from "@/components/steel"
import { Input } from "@/components/ui/input"

type Props = {
  email: string
  name: string
}

export default function PasswordEdit({ email, name }: Props) {
  const [password, setPassword] = useState("")
  const [confirmation, setConfirmation] = useState("")

  const mismatch = confirmation.length > 0 && password !== confirmation
  const tooShort = password.length > 0 && password.length < 6

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground">
      <Head title="Set your password" />

      <div className="w-full max-w-[380px]">
        <div className="mb-5 flex items-center gap-2.5">
          <span className="flex h-[26px] w-[26px] items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Building2 className="h-[15px] w-[15px]" strokeWidth={1.75} />
          </span>
          <span className="text-label font-semibold tracking-[-0.01em]">SteelTrack</span>
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <h1 className="text-title font-semibold">Welcome, {name}</h1>
          <p className="mt-1.5 text-label text-muted-foreground">
            You signed in with a temporary password. Pick your own to continue.
          </p>

          <form
            className="mt-4 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (password.length < 6 || mismatch) return
              router.patch("/password", { password, passwordConfirmation: confirmation })
            }}
          >
            <Field label="Email">
              <Input value={email} readOnly disabled />
            </Field>
            <Field label="New password">
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Field label="Confirm password">
              <Input
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
              />
            </Field>

            {mismatch ? (
              <p className="text-meta text-destructive">Those passwords do not match.</p>
            ) : tooShort ? (
              <p className="text-meta text-destructive">At least 6 characters.</p>
            ) : (
              <p className="text-meta text-muted-foreground">At least 6 characters.</p>
            )}

            <Button type="submit" size="sm" disabled={password.length < 6 || mismatch}>
              Save password
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}

PasswordEdit.layout = (page: ReactNode) => page
